import React, { useState, useEffect, useContext, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Select from 'react-select';
import html2pdf from 'html2pdf.js';
import { LoginContext } from '../../reactcontext/ReactContext';
import { years } from '../../data/options';

const Invoice = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const { loginInfo } = useContext(LoginContext);
  const [paymentRecords, setPaymentRecords] = useState([]);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [passportOptions, setPassportOptions] = useState([]);
  const [agents, setAgents] = useState([]);
  
  // Individual Invoice Section
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedPassport, setSelectedPassport] = useState(null);
  const [selectedYear, setSelectedYear] = useState({ value: new Date().getFullYear().toString(), label: new Date().getFullYear().toString() });
  const [invoiceData, setInvoiceData] = useState(null);
  const [loading, setLoading] = useState(false);
  const invoiceRef = useRef();

  // Agent Section
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [selectedAgentYear, setSelectedAgentYear] = useState({ value: new Date().getFullYear().toString(), label: new Date().getFullYear().toString() });
  const [agentInvoiceData, setAgentInvoiceData] = useState(null);
  const agentInvoiceRef = useRef();

  useEffect(() => {
    if (!loginInfo.login) return;
    fetchPaymentRecords();
  }, [loginInfo]);

  const fetchPaymentRecords = async () => {
    try {
      const res = await axios.get(`${apiUrl}/payment/getAllPaymentsWithBalance`, {
        headers: { loginToken: localStorage.getItem('loginToken') },
      });
      const records = Array.isArray(res.data) ? res.data : [];
      setPaymentRecords(records);

      // Extract all unique employee-passport combinations (including if same name appears in multiple years)
      const uniqueEmployeePassportMap = new Map();
      records.forEach(r => {
        const passportNo = r.Employee?.Passports?.[0]?.passportNo || r.passportNumber;
        if (r.Employee?.id) {
          const key = `${r.Employee.id}-${passportNo || 'na'}-${r.year}`;
          if (!uniqueEmployeePassportMap.has(key)) {
            uniqueEmployeePassportMap.set(key, {
              value: r.Employee.id,
              employeeId: r.Employee.id,
              label: `${r.Employee.firstName} ${r.Employee.lastName}`,
              data: r.Employee,
              passport: passportNo,
              year: r.year,
            });
          }
        }
      });
      
      // Group by name to allow multiple entries for same name
      const empOptions = Array.from(uniqueEmployeePassportMap.values())
        .sort((a, b) => a.label.localeCompare(b.label));
      setEmployeeOptions(empOptions);

      // Extract all unique passport-employee combinations
      const passportMap = new Map();
      records.forEach(r => {
        const passportNo = r.Employee?.Passports?.[0]?.passportNo || r.passportNumber;
        if (passportNo && r.Employee?.id) {
          if (!passportMap.has(passportNo)) {
            passportMap.set(passportNo, {
              value: passportNo,
              label: passportNo,
              employeeIds: new Set(),
              employeeNames: new Set(),
            });
          }
          passportMap.get(passportNo).employeeIds.add(r.Employee.id);
          passportMap.get(passportNo).employeeNames.add(`${r.Employee.firstName} ${r.Employee.lastName}`);
        }
      });
      
      const passportOpts = Array.from(passportMap.values()).map(p => ({
        value: p.value,
        label: p.label,
        employeeIds: Array.from(p.employeeIds),
        employeeNames: Array.from(p.employeeNames),
      }));
      setPassportOptions(passportOpts);

      // Fetch all agents from backend instead of just extracting from records
      try {
        const agentRes = await axios.get(`${apiUrl}/agent`, {
          headers: { loginToken: localStorage.getItem('loginToken') },
        });
        const allAgents = Array.isArray(agentRes.data) ? agentRes.data : [];
        const agentOpts = allAgents.map(ag => ({
          value: ag.id,
          label: ag.name,
        }));
        console.log('All agents fetched:', agentOpts);
        setAgents(agentOpts);
      } catch (agentErr) {
        console.error('Error fetching agents:', agentErr);
        // Fallback to extracting from records if fetching fails
        const uniqueAgentsMap = new Map();
        records.forEach(r => {
          if (r.Employee?.Agent?.id) {
            uniqueAgentsMap.set(r.Employee.Agent.id, r.Employee.Agent);
          }
        });
        const agentOpts = Array.from(uniqueAgentsMap.values()).map(ag => ({
          value: ag.id,
          label: ag.name,
        }));
        setAgents(agentOpts);
      }
    } catch (err) {
      console.error('Error fetching payment records:', err);
    }
  };


  // Get filtered passport options based on selected employee
  const getFilteredPassportOptions = () => {
    if (selectedEmployee) {
      // Show all passports that have this employee ID or name
      return passportOptions.filter(p => 
        p.employeeIds.includes(selectedEmployee.value) || 
        p.employeeNames.some(name => name === selectedEmployee.label)
      );
    }
    return passportOptions;
  };

  // Get filtered employee options based on selected passport
  const getFilteredEmployeeOptions = () => {
    if (selectedPassport) {
      // Show all employees that have this passport
      return employeeOptions.filter(e => selectedPassport.employeeIds.includes(e.value));
    }
    return employeeOptions;
  };

  // Handle employee selection
  const handleEmployeeSelect = (option) => {
    setSelectedEmployee(option);
    // If new employee is selected, check if current passport is compatible
    if (option && selectedPassport) {
      const isCompatible = passportOptions.find(p => 
        p.value === selectedPassport.value && 
        (p.employeeIds.includes(option.value) || p.employeeNames.some(name => name === option.label))
      );
      if (!isCompatible) {
        setSelectedPassport(null);
      }
    } else if (!option) {
      setSelectedPassport(null);
    }
  };

  // Handle passport selection
  const handlePassportSelect = (option) => {
    setSelectedPassport(option);
    // If new passport is selected, check if current employee is compatible
    if (option && selectedEmployee) {
      const isCompatible = option.employeeIds.includes(selectedEmployee.value) || 
                          option.employeeNames.some(name => name === selectedEmployee.label);
      if (!isCompatible) {
        setSelectedEmployee(null);
      }
    } else if (!option) {
      setSelectedEmployee(null);
    }
  };

  const generateInvoice = async () => {
    if (!selectedEmployee || !selectedYear) {
      alert('Please select an employee and year');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(`${apiUrl}/payment/getPaymentsWithBalance/${selectedEmployee.value}/${selectedYear.value}`, {
        headers: { loginToken: localStorage.getItem('loginToken') },
      });
      if (res.data.error) {
        alert('No payment data found');
        setInvoiceData(null);
      } else {
        const initialPaymentRecord = paymentRecords.find(
          r => r.Employee?.id === selectedEmployee.value && r.year === selectedYear.value
        );
        const initialAmount = parseFloat(initialPaymentRecord?.initialAmount || 0);
        const totalPaid = parseFloat(res.data.totalPayments || 0);
        setInvoiceData({
          employee: res.data.employee || {},
          payments: res.data.payments,
          totalPaid,
          balance: initialAmount - totalPaid,
          initialAmount,
        });
      }
    } catch (err) {
      console.error(err);
      alert('Error fetching data');
    }
    setLoading(false);
  };

  const printInvoice = () => {
    if (!invoiceRef.current) return;
    
    const opt = {
      margin: [10, 10, 10, 10],
      filename: `Invoice_${invoiceData.employee.Passports?.[0]?.passportNo || 'employee'}_${selectedYear.value}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
    };
    
    html2pdf().set(opt).from(invoiceRef.current).save();
  };

  const bulkDownload = async () => {
    if (!selectedAgent || !selectedAgentYear) {
      alert('Please select an agent and year');
      return;
    }
    
    // Get all employees under this agent
    const agentEmployees = paymentRecords.filter(r => 
      r.Employee?.Agent?.id === selectedAgent.value && r.year === selectedAgentYear.value
    );

    if (agentEmployees.length === 0) {
      alert('No employees found for this agent in the selected year');
      return;
    }

    // Calculate totals
    let totalAmount = 0;
    let totalPaid = 0;
    let totalBalance = 0;

    const employeeDetails = agentEmployees.map(record => {
      const amount = parseFloat(record.initialAmount || 0);
      const paid = parseFloat(record.totalPayments || 0);
      const balance = parseFloat(record.balance || 0);
      
      totalAmount += amount;
      totalPaid += paid;
      totalBalance += balance;

      return {
        name: `${record.Employee?.firstName} ${record.Employee?.lastName}`,
        passport: record.Employee?.Passports?.[0]?.passportNo || record.passportNumber || 'N/A',
        nationality: record.Employee?.nationality,
        initialAmount: amount,
        totalPaid: paid,
        balance: balance,
      };
    });

    setAgentInvoiceData({
      agentName: selectedAgent.label,
      year: selectedAgentYear.value,
      employeeCount: agentEmployees.length,
      employees: employeeDetails,
      totalAmount,
      totalPaid,
      totalBalance,
    });
  };

  const printAgentInvoice = () => {
    if (!agentInvoiceRef.current) return;
    
    const opt = {
      margin: [8, 8, 15, 8],
      filename: `Agent_Summary_${selectedAgent.label}_${selectedAgentYear.value}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' },
      pagebreak: { mode: ['css', 'legacy'] }
    };
    
    html2pdf().set(opt).from(agentInvoiceRef.current).save();
  };

  const yearOptions = years.map(y => ({ value: y.toString(), label: y.toString() }));

  return (
    <div className='w-full bg-gray-50 min-h-screen flex flex-col'>
      {/* Header */}
      <div className='bg-white shadow-sm border-b border-gray-200'>
        <div className='max-w-7xl mx-auto px-6 py-4'>
          <div className='flex items-center gap-2 text-sm text-gray-600 mb-2'>
            <Link to={'/payments'} className='text-blue-600 hover:text-blue-800'>Payments</Link>
            <span className='text-gray-400'>{'>'}</span>
            <span>Generate Invoice</span>
          </div>
          <h1 className='text-3xl font-bold text-gray-900'>Generate Invoice</h1>
          <p className='text-gray-600 mt-1'>Create and download invoices for employees or bulk download by agent</p>
        </div>
      </div>

      {/* Main Content */}
      <div className='flex-1 max-w-7xl mx-auto w-full px-6 py-8'>
        {/* Section 1: Individual Invoice */}
        <div className='mb-12'>
          <div className='bg-white rounded-lg shadow-md border border-gray-200'>
            {/* Section Header */}
            <div className='bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 border-b border-blue-800'>
              <h2 className='text-2xl font-bold text-white flex items-center gap-2'>
                <span className='text-3xl'>📄</span> Individual Invoice
              </h2>
              <p className='text-blue-100 mt-1 text-sm'>Filter by name, passport number, or year</p>
            </div>

            {/* Filter Section */}
            <div className='p-6 bg-gray-50 border-b border-gray-200 relative z-20'>
              <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4'>
                <div className='relative z-40'>
                  <label className='block text-sm font-semibold text-gray-700 mb-2'>Employee Name</label>
                  <Select
                    options={getFilteredEmployeeOptions()}
                    value={selectedEmployee}
                    onChange={handleEmployeeSelect}
                    placeholder="Search by name..."
                    isClearable
                    isSearchable
                    styles={{
                      control: (base) => ({ 
                        ...base, 
                        minHeight: 42,
                        borderColor: '#d1d5db',
                        '&:hover': { borderColor: '#9ca3af' }
                      }),
                      menu: (base) => ({ 
                        ...base, 
                        zIndex: 9999, 
                        position: 'absolute',
                        top: '100%'
                      }),
                      menuList: (base) => ({
                        ...base,
                        maxHeight: 200,
                        overflow: 'auto'
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isSelected ? '#3b82f6' : state.isFocused ? '#e5e7eb' : 'white',
                        color: state.isSelected ? 'white' : '#1f2937',
                        cursor: 'pointer',
                        padding: '10px 12px'
                      })
                    }}
                  />
                </div>
                <div className='relative z-30'>
                  <label className='block text-sm font-semibold text-gray-700 mb-2'>Passport Number</label>
                  <Select
                    options={getFilteredPassportOptions()}
                    value={selectedPassport}
                    onChange={handlePassportSelect}
                    placeholder="Search by passport..."
                    isClearable
                    isSearchable
                    styles={{
                      control: (base) => ({ 
                        ...base, 
                        minHeight: 42,
                        borderColor: '#d1d5db',
                        '&:hover': { borderColor: '#9ca3af' }
                      }),
                      menu: (base) => ({ 
                        ...base, 
                        zIndex: 9998, 
                        position: 'absolute',
                        top: '100%'
                      }),
                      menuList: (base) => ({
                        ...base,
                        maxHeight: 200,
                        overflow: 'auto'
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isSelected ? '#3b82f6' : state.isFocused ? '#e5e7eb' : 'white',
                        color: state.isSelected ? 'white' : '#1f2937',
                        cursor: 'pointer',
                        padding: '10px 12px'
                      })
                    }}
                  />
                </div>
                <div className='relative z-20'>
                  <label className='block text-sm font-semibold text-gray-700 mb-2'>Year</label>
                  <Select
                    options={yearOptions}
                    value={selectedYear}
                    onChange={setSelectedYear}
                    placeholder="Select year..."
                    styles={{
                      control: (base) => ({ 
                        ...base, 
                        minHeight: 42,
                        borderColor: '#d1d5db',
                        '&:hover': { borderColor: '#9ca3af' }
                      }),
                      menu: (base) => ({ 
                        ...base, 
                        zIndex: 9997, 
                        position: 'absolute',
                        top: '100%'
                      }),
                      menuList: (base) => ({
                        ...base,
                        maxHeight: 200,
                        overflow: 'auto'
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isSelected ? '#3b82f6' : state.isFocused ? '#e5e7eb' : 'white',
                        color: state.isSelected ? 'white' : '#1f2937',
                        cursor: 'pointer',
                        padding: '10px 12px'
                      })
                    }}
                  />
                </div>
                <div className='flex items-end relative z-10'>
                  <button
                    onClick={generateInvoice}
                    disabled={loading || !selectedEmployee}
                    className='w-full bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium transition'
                  >
                    {loading ? 'Generating...' : 'Generate Invoice'}
                  </button>
                </div>
              </div>
            </div>

            {/* Invoice Display */}
            {invoiceData && (
              <div className='p-6 relative z-0'>
                <div ref={invoiceRef} className='bg-white p-12 rounded' style={{ fontFamily: 'Arial, sans-serif' }}>
                  {/* Company Header */}
                  <div className='mb-8 pb-6 border-b-4' style={{ borderColor: '#1e40af' }}>
                    <div className='flex justify-between items-start mb-4'>
                      <div>
                        <h1 style={{ fontSize: '32px', fontWeight: 'bold', color: '#1e40af', margin: '0' }}>RAHMAN GROUP</h1>
                        <p style={{ color: '#6b7280', margin: '4px 0 0 0', fontSize: '14px' }}>Employee Payment Services</p>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#1e40af', margin: '0' }}>e-INVOICE</p>
                        <p style={{ color: '#6b7280', margin: '4px 0', fontSize: '12px' }}>Digital Payment Statement</p>
                      </div>
                    </div>
                  </div>

                  {/* Invoice Details Header */}
                  <div className='mb-8' style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                    <div>
                      <p style={{ margin: '0 0 8px 0', fontWeight: 'bold' }}>INVOICE DETAILS</p>
                      <p style={{ margin: '4px 0', color: '#6b7280' }}>Invoice Date: <span style={{ fontWeight: 'bold', color: '#1f2937' }}>{new Date().toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' })}</span></p>
                      <p style={{ margin: '4px 0', color: '#6b7280' }}>Payment Year: <span style={{ fontWeight: 'bold', color: '#1f2937' }}>{selectedYear.value}</span></p>
                      <p style={{ margin: '4px 0', color: '#6b7280' }}>Invoice ID: <span style={{ fontWeight: 'bold', color: '#1f2937' }}>INV-{invoiceData.employee.Passports?.[0]?.passportNo || invoiceData.employee.id}-{selectedYear.value}</span></p>
                    </div>
                    <div className='text-right'>
                      <p style={{ margin: '0 0 8px 0', fontWeight: 'bold' }}>COMPANY INFO</p>
                      <p style={{ margin: '4px 0', fontSize: '12px', color: '#6b7280' }}>Rahman Group International</p>
                      <p style={{ margin: '4px 0', fontSize: '12px', color: '#6b7280' }}>Employment Services Provider</p>
                      <p style={{ margin: '4px 0', fontSize: '12px', color: '#6b7280' }}>Malaysia</p>
                    </div>
                  </div>

                  {/* Employee Information Section */}
                  <div className='mb-8' style={{ display: 'flex', gap: '40px' }}>
                    <div style={{ flex: 1 }}>
                      <p style={{ margin: '0 0 8px 0', fontWeight: 'bold', color: '#1f2937', fontSize: '13px', textTransform: 'uppercase' }}>Bill To / Employee</p>
                      <div style={{ 
                        padding: '12px', 
                        border: '1px solid #e5e7eb', 
                        borderRadius: '4px',
                        backgroundColor: '#f9fafb'
                      }}>
                        <p style={{ margin: '0 0 6px 0', fontWeight: 'bold', fontSize: '15px', color: '#1f2937' }}>
                          {invoiceData.employee.firstName} {invoiceData.employee.lastName}
                        </p>
                        <p style={{ margin: '4px 0', fontSize: '13px', color: '#6b7280' }}>
                          <span style={{ fontWeight: 'bold' }}>Passport No:</span> {invoiceData.employee.Passports?.[0]?.passportNo || 'N/A'}
                        </p>
                        <p style={{ margin: '4px 0', fontSize: '13px', color: '#6b7280' }}>
                          <span style={{ fontWeight: 'bold' }}>Nationality:</span> {invoiceData.employee.nationality}
                        </p>
                        <p style={{ margin: '4px 0', fontSize: '13px', color: '#6b7280' }}>
                          <span style={{ fontWeight: 'bold' }}>Agent:</span> {invoiceData.employee.Agent?.name || 'N/A'}
                        </p>
                      </div>
                    </div>
                    
                    <div style={{ flex: 1 }}>
                      <p style={{ margin: '0 0 8px 0', fontWeight: 'bold', color: '#1f2937', fontSize: '13px', textTransform: 'uppercase' }}>Financial Summary</p>
                      <div style={{
                        padding: '12px',
                        backgroundColor: '#eff6ff',
                        border: '2px solid #3b82f6',
                        borderRadius: '4px'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', margin: '6px 0', fontSize: '13px' }}>
                          <span style={{ color: '#6b7280' }}>Initial Amount:</span>
                          <span style={{ fontWeight: 'bold', color: '#1f2937' }}>RM {parseFloat(invoiceData.initialAmount || 0).toFixed(2)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', margin: '6px 0', fontSize: '13px' }}>
                          <span style={{ color: '#6b7280' }}>Total Paid:</span>
                          <span style={{ fontWeight: 'bold', color: '#10b981' }}>RM {parseFloat(invoiceData.totalPaid || 0).toFixed(2)}</span>
                        </div>
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          margin: '8px 0 0 0', 
                          paddingTop: '8px',
                          borderTop: '2px solid #bfdbfe',
                          fontSize: '14px'
                        }}>
                          <span style={{ fontWeight: 'bold', color: '#1f2937' }}>Outstanding Balance:</span>
                          <span style={{ fontWeight: 'bold', color: '#dc2626', fontSize: '16px' }}>RM {parseFloat(invoiceData.balance || 0).toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Payment History Table */}
                  {invoiceData.payments && invoiceData.payments.length > 0 && (
                    <div className='mb-8'>
                      <p style={{ margin: '0 0 12px 0', fontWeight: 'bold', color: '#1f2937', fontSize: '13px', textTransform: 'uppercase' }}>Payment Transaction Details</p>
                      <table style={{ 
                        width: '100%', 
                        borderCollapse: 'collapse',
                        border: '1px solid #e5e7eb'
                      }}>
                        <thead>
                          <tr style={{ backgroundColor: '#1e40af' }}>
                            <th style={{ 
                              padding: '10px 12px', 
                              textAlign: 'left', 
                              fontWeight: 'bold',
                              color: 'white',
                              fontSize: '12px',
                              borderRight: '1px solid #1e3a8a'
                            }}>Transaction Id</th>
                            <th style={{ 
                              padding: '10px 12px', 
                              textAlign: 'left', 
                              fontWeight: 'bold',
                              color: 'white',
                              fontSize: '12px',
                              borderRight: '1px solid #1e3a8a'
                            }}>Payment Date</th>
                            <th style={{ 
                              padding: '10px 12px', 
                              textAlign: 'right', 
                              fontWeight: 'bold',
                              color: 'white',
                              fontSize: '12px',
                              borderRight: '1px solid #1e3a8a'
                            }}>Amount (RM)</th>
                            <th style={{ 
                              padding: '10px 12px', 
                              textAlign: 'left', 
                              fontWeight: 'bold',
                              color: 'white',
                              fontSize: '12px',
                              borderRight: '1px solid #1e3a8a'
                            }}>Payment Mode</th>
                            <th style={{ 
                              padding: '10px 12px', 
                              textAlign: 'left', 
                              fontWeight: 'bold',
                              color: 'white',
                              fontSize: '12px'
                            }}>Reference</th>
                          </tr>
                        </thead>
                        <tbody>
                          {invoiceData.payments.map((payment, idx) => (
                            <tr key={idx} style={{ 
                              backgroundColor: idx % 2 === 0 ? '#f9fafb' : '#ffffff',
                              borderBottom: '1px solid #e5e7eb'
                            }}>
                              <td style={{ 
                                padding: '10px 12px', 
                                fontSize: '12px',
                                color: '#1f2937',
                                fontWeight: 'bold'
                              }}>{idx + 1}</td>
                              <td style={{ 
                                padding: '10px 12px', 
                                fontSize: '12px',
                                color: '#6b7280'
                              }}>{payment.dateOfPayment}</td>
                              <td style={{ 
                                padding: '10px 12px', 
                                fontSize: '12px',
                                color: '#10b981',
                                fontWeight: 'bold',
                                textAlign: 'right'
                              }}>RM {parseFloat(payment.amount).toFixed(2)}</td>
                              <td style={{ 
                                padding: '10px 12px', 
                                fontSize: '12px',
                                color: '#6b7280'
                              }}>{payment.paymentMode}</td>
                              <td style={{ 
                                padding: '10px 12px', 
                                fontSize: '12px',
                                color: '#6b7280'
                              }}>{payment.referenceNumber || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* No Payments Message */}
                  {(!invoiceData.payments || invoiceData.payments.length === 0) && (
                    <div style={{ 
                      padding: '16px', 
                      backgroundColor: '#fef3c7',
                      border: '1px solid #fcd34d',
                      borderRadius: '4px',
                      marginBottom: '16px'
                    }}>
                      <p style={{ margin: '0', color: '#92400e', fontSize: '13px' }}>ℹ️ No payment transactions recorded for this period.</p>
                    </div>
                  )}

                  {/* Footer */}
                  <div style={{ 
                    marginTop: '32px', 
                    paddingTop: '16px',
                    borderTop: '1px solid #e5e7eb',
                    fontSize: '11px',
                    color: '#9ca3af',
                    textAlign: 'center'
                  }}>
                    <p style={{ margin: '4px 0' }}>This is an electronically generated invoice. No signature required.</p>
                    <p style={{ margin: '4px 0' }}>© 2026 Rahman Group. All rights reserved.</p>
                    <p style={{ margin: '4px 0' }}>Generated on: {new Date().toLocaleString('en-GB')}</p>
                  </div>
                </div>

                <button
                  onClick={printInvoice}
                  className='mt-6 bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 font-medium transition flex items-center gap-2'
                >
                  📥 Download as PDF
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Section 2: Agent E-Invoice Summary */}
        <div>
          <div className='bg-white rounded-lg shadow-md border border-gray-200'>
            {/* Section Header */}
            <div className='bg-gradient-to-r from-green-600 to-green-700 px-6 py-4 border-b border-green-800'>
              <h2 className='text-2xl font-bold text-white flex items-center gap-2'>
                <span className='text-3xl'>📊</span> Agent Payment Summary
              </h2>
              <p className='text-green-100 mt-1 text-sm'>View e-invoice for all employees under an agent</p>
            </div>

            {/* Filter Section */}
            <div className='p-6 bg-gray-50 border-b border-gray-200 relative z-10'>
              <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                <div className='relative z-30'>
                  <label className='block text-sm font-semibold text-gray-700 mb-2'>Select Agent ({agents.length})</label>
                  <Select
                    options={agents}
                    value={selectedAgent}
                    onChange={setSelectedAgent}
                    placeholder="Search by agent name..."
                    isClearable
                    isSearchable
                    menuPortalTarget={document.body}
                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: 42,
                        borderColor: '#d1d5db',
                        '&:hover': { borderColor: '#9ca3af' }
                      }),
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      menu: (base) => ({ ...base, zIndex: 9999 }),
                      menuList: (base) => ({ ...base, maxHeight: 200 }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isSelected ? '#3b82f6' : state.isFocused ? '#e5e7eb' : '#ffffff',
                        color: state.isSelected ? '#ffffff' : '#1f2937',
                        cursor: 'pointer',
                        padding: '10px 12px',
                        fontWeight: 'normal',
                      }),
                      singleValue: (base) => ({ ...base, color: '#1f2937' }),
                    }}
                  />
                </div>
                <div className='relative z-20'>
                  <label className='block text-sm font-semibold text-gray-700 mb-2'>Year</label>
                  <Select
                    options={yearOptions}
                    value={selectedAgentYear}
                    onChange={setSelectedAgentYear}
                    placeholder="Select year..."
                    styles={{
                      control: (base) => ({ 
                        ...base, 
                        minHeight: 42,
                        borderColor: '#d1d5db',
                        '&:hover': { borderColor: '#9ca3af' }
                      }),
                      menu: (base) => ({ 
                        ...base, 
                        zIndex: 9998, 
                        position: 'absolute',
                        top: '100%'
                      }),
                      menuList: (base) => ({
                        ...base,
                        maxHeight: 200,
                        overflow: 'auto'
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isSelected ? '#3b82f6' : state.isFocused ? '#e5e7eb' : 'white',
                        color: state.isSelected ? 'white' : '#1f2937',
                        cursor: 'pointer',
                        padding: '10px 12px'
                      })
                    }}
                  />
                </div>
                <div className='flex items-end relative z-10'>
                  <button
                    onClick={bulkDownload}
                    disabled={!selectedAgent}
                    className='w-full bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium transition'
                  >
                    �️ Generate Agent Invoice
                  </button>
                </div>
              </div>
            </div>

            {/* Invoice Display or Info */}
            <div className='p-6'>
              {agentInvoiceData ? (
                <div>
                  {/* Agent Invoice Template */}
                  <div ref={agentInvoiceRef} style={{
                    backgroundColor: '#ffffff',
                    padding: '20px',
                    fontFamily: 'Arial, sans-serif',
                    color: '#1f2937',
                    marginBottom: '24px'
                  }}>
                    {/* Header */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '12px',
                      paddingBottom: '8px',
                      borderBottom: '2px solid #1e40af'
                    }}>
                      <div style={{ flex: 1 }}>
                        <h1 style={{ 
                          margin: '0 0 2px 0', 
                          fontSize: '18px', 
                          fontWeight: 'bold',
                          color: '#1e40af'
                        }}>E-INVOICE</h1>
                        <p style={{ margin: '2px 0', fontSize: '10px', color: '#6b7280' }}>Agent Payment Summary</p>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <h2 style={{ 
                          margin: '0 0 2px 0', 
                          fontSize: '14px', 
                          fontWeight: 'bold',
                          color: '#1f2937'
                        }}>Rahman Group</h2>
                        <p style={{ margin: '0px 0', fontSize: '10px', color: '#6b7280' }}>Employment Services</p>
                      </div>
                    </div>

                    {/* Invoice Details */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: '12px',
                      fontSize: '11px'
                    }}>
                      <div>
                        <p style={{ margin: '0 0 4px 0', fontWeight: 'bold', fontSize: '11px' }}>AGENT INFO</p>
                        <div style={{
                          padding: '6px',
                          backgroundColor: '#f3f4f6',
                          borderLeft: '3px solid #1e40af',
                          borderRadius: '3px'
                        }}>
                          <p style={{ margin: '2px 0', fontWeight: 'bold', fontSize: '12px', color: '#1f2937' }}>
                            {agentInvoiceData.agentName}
                          </p>
                          <p style={{ margin: '2px 0', color: '#6b7280', fontSize: '10px' }}>
                            <span style={{ fontWeight: 'bold' }}>Year:</span> {agentInvoiceData.year}
                          </p>
                          <p style={{ margin: '2px 0', color: '#6b7280', fontSize: '10px' }}>
                            <span style={{ fontWeight: 'bold' }}>Employees:</span> {agentInvoiceData.employeeCount}
                          </p>
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <p style={{ margin: '0 0 4px 0', fontWeight: 'bold', fontSize: '11px' }}>DETAILS</p>
                        <div style={{ 
                          padding: '6px',
                          backgroundColor: '#fef3c7',
                          borderLeft: '3px solid #f59e0b',
                          borderRadius: '3px',
                          textAlign: 'right'
                        }}>
                          <p style={{ margin: '2px 0', color: '#6b7280', fontSize: '9px' }}>Generated: {new Date().toLocaleDateString('en-GB')}</p>
                          <p style={{ margin: '2px 0', color: '#6b7280', fontSize: '9px' }}>ID: INV-AGT-{selectedAgent?.value}-{agentInvoiceData.year}</p>
                        </div>
                      </div>
                    </div>

                    {/* Employee Table */}
                    <div style={{ marginBottom: '12px' }}>
                      <p style={{ 
                        margin: '0 0 4px 0', 
                        fontWeight: 'bold', 
                        fontSize: '11px',
                        textTransform: 'uppercase',
                        color: '#1f2937'
                      }}>EMPLOYEE DETAILS</p>
                      <table style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        border: '1px solid #e5e7eb',
                        fontSize: '10px'
                      }}>
                        <thead>
                          <tr style={{ backgroundColor: '#1e40af' }}>
                            <th style={{
                              padding: '4px 6px',
                              textAlign: 'left',
                              fontWeight: 'bold',
                              color: 'white',
                              fontSize: '10px',
                              borderRight: '1px solid #1e3a8a'
                            }}>Employee Name</th>
                            <th style={{
                              padding: '4px 6px',
                              textAlign: 'left',
                              fontWeight: 'bold',
                              color: 'white',
                              fontSize: '10px',
                              borderRight: '1px solid #1e3a8a'
                            }}>Passport</th>
                            <th style={{
                              padding: '4px 6px',
                              textAlign: 'left',
                              fontWeight: 'bold',
                              color: 'white',
                              fontSize: '10px',
                              borderRight: '1px solid #1e3a8a'
                            }}>Nationality</th>
                            <th style={{
                              padding: '4px 6px',
                              textAlign: 'right',
                              fontWeight: 'bold',
                              color: 'white',
                              fontSize: '10px',
                              borderRight: '1px solid #1e3a8a'
                            }}>Initial</th>
                            <th style={{
                              padding: '4px 6px',
                              textAlign: 'right',
                              fontWeight: 'bold',
                              color: 'white',
                              fontSize: '10px',
                              borderRight: '1px solid #1e3a8a'
                            }}>Paid</th>
                            <th style={{
                              padding: '4px 6px',
                              textAlign: 'right',
                              fontWeight: 'bold',
                              color: 'white',
                              fontSize: '10px'
                            }}>Balance</th>
                          </tr>
                        </thead>
                        <tbody>
                          {agentInvoiceData.employees.map((emp, idx) => (
                            <tr key={idx} style={{
                              backgroundColor: idx % 2 === 0 ? '#ffffff' : '#f9fafb',
                              borderBottom: '1px solid #e5e7eb'
                            }}>
                              <td style={{ padding: '3px 6px', fontSize: '10px', fontWeight: '500' }}>{emp.name}</td>
                              <td style={{ padding: '3px 6px', fontSize: '10px' }}>{emp.passport}</td>
                              <td style={{ padding: '3px 6px', fontSize: '10px' }}>{emp.nationality}</td>
                              <td style={{ padding: '3px 6px', fontSize: '10px', textAlign: 'right', fontWeight: 'bold' }}>RM {emp.initialAmount.toFixed(2)}</td>
                              <td style={{ padding: '3px 6px', fontSize: '10px', textAlign: 'right', color: '#059669', fontWeight: 'bold' }}>RM {emp.totalPaid.toFixed(2)}</td>
                              <td style={{ 
                                padding: '3px 6px', 
                                fontSize: '10px', 
                                textAlign: 'right', 
                                fontWeight: 'bold',
                                color: emp.balance > 0 ? '#dc2626' : '#059669'
                              }}>RM {emp.balance.toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Summary */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      marginBottom: '20px',
                      marginTop: '20px'
                    }}>
                      <div style={{
                        width: '300px',
                        backgroundColor: '#eff6ff',
                        border: '2px solid #3b82f6',
                        borderRadius: '6px',
                        padding: '10px'
                      }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          marginBottom: '6px',
                          paddingBottom: '6px',
                          borderBottom: '1px solid #bfdbfe',
                          fontSize: '10px'
                        }}>
                          <span style={{ color: '#6b7280' }}>Total Initial:</span>
                          <span style={{ fontWeight: 'bold', color: '#1f2937' }}>RM {agentInvoiceData.totalAmount.toFixed(2)}</span>
                        </div>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          marginBottom: '6px',
                          paddingBottom: '6px',
                          borderBottom: '1px solid #bfdbfe',
                          fontSize: '10px'
                        }}>
                          <span style={{ color: '#6b7280' }}>Total Paid:</span>
                          <span style={{ fontWeight: 'bold', color: '#059669' }}>RM {agentInvoiceData.totalPaid.toFixed(2)}</span>
                        </div>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontSize: '11px'
                        }}>
                          <span style={{ fontWeight: 'bold', color: '#1f2937' }}>Balance:</span>
                          <span style={{ 
                            fontWeight: 'bold', 
                            color: agentInvoiceData.totalBalance > 0 ? '#dc2626' : '#059669',
                            fontSize: '12px'
                          }}>RM {agentInvoiceData.totalBalance.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Spacer to push footer down */}
                    <div style={{ height: '80px' }}></div>

                    {/* Footer */}
                    <div style={{
                      paddingTop: '12px',
                      borderTop: '1px solid #e5e7eb',
                      fontSize: '9px',
                      color: '#9ca3af',
                      textAlign: 'center'
                    }}>
                      <p style={{ margin: '2px 0' }}>This invoice was electronically generated. No signature required.</p>
                      <p style={{ margin: '2px 0' }}>© 2026 Rahman Group. All rights reserved. Generated: {new Date().toLocaleString('en-GB')}</p>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button
                      onClick={printAgentInvoice}
                      className='flex-1 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 font-medium transition flex items-center justify-center gap-2'
                    >
                      📥 Download as PDF
                    </button>
                    <button
                      onClick={() => setAgentInvoiceData(null)}
                      className='flex-1 bg-gray-300 text-gray-800 px-6 py-3 rounded-lg hover:bg-gray-400 font-medium transition flex items-center justify-center gap-2'
                    >
                      ✕ Clear
                    </button>
                  </div>
                </div>
              ) : (
                <div className='bg-blue-50 border border-blue-200 rounded p-4'>
                  <p className='text-blue-900 text-sm'>
                    <span className='font-semibold'>ℹ️ Agent Summary:</span> Select an agent and year to generate a professional e-invoice showing all employees under that agent with their payment details and outstanding balances.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Invoice;